import { defaultAxiosInstance } from '../config/axiosConfig';

/**
 * Platform Admin console service (spec §4).
 *
 * Auth model: the logged-in platform admin's own key (x-api-key via
 * defaultAxiosInstance). The console route is already gated on `is_platform_admin`
 * (Phase 1), so no separate x-admin-key prompt is needed.
 *
 * List endpoints accept `search` / `limit` (default 50, max 200) / `offset`
 * (default 0) and return `total` (+ echoed limit/offset). All methods return
 * `response.data`; callers normalize shape.
 */

// Build the shared pagination/search query params (omitting empties).
const listParams = ({ search, limit, offset } = {}) => {
  const params = {};
  if (search) params.search = search;
  if (limit != null) params.limit = limit;
  if (offset != null) params.offset = offset;
  return params;
};

class PlatformAdminService {
  // ── Companies (§4.1) ─────────────────────────────────────────────────────
  /** → { companies, total, limit, offset } (searches company name) */
  async listCompanies(activeOnly = false, opts = {}) {
    const res = await defaultAxiosInstance.get('/companies/', {
      params: { active_only: activeOnly, ...listParams(opts) },
    });
    return res.data;
  }

  async getCompany(companyId) {
    const res = await defaultAxiosInstance.get(`/companies/${companyId}`);
    return res.data;
  }

  async createCompany(payload) {
    const res = await defaultAxiosInstance.post('/companies/', payload);
    return res.data;
  }

  async updateCompany(companyId, payload) {
    const res = await defaultAxiosInstance.put(`/companies/${companyId}`, payload);
    return res.data;
  }

  /** Soft-deactivate a company (locks out all its users). */
  async deleteCompany(companyId) {
    const res = await defaultAxiosInstance.delete(`/companies/${companyId}`);
    return res.data;
  }

  async assignCompanyUser(companyId, payload) {
    const res = await defaultAxiosInstance.post(`/companies/${companyId}/users`, payload);
    return res.data;
  }

  async unassignCompanyUser(companyId, userId) {
    const res = await defaultAxiosInstance.delete(`/companies/${companyId}/users/${userId}`);
    return res.data;
  }

  /** → credential rows (array, or { credentials }). For a partner's home company these are the partner's keys. */
  async getCompanyLLMKeys(companyId) {
    const res = await defaultAxiosInstance.get(`/companies/${companyId}/llm-keys`);
    return res.data;
  }

  /**
   * Body: any subset of { api_key ('' clears), base_url, allow_platform_fallback,
   * inherit_from_partner }. `inherit_from_partner` only matters for a partner's
   * client company; its own `allow_platform_fallback` is ignored while it has one.
   */
  async setCompanyLLMKey(companyId, providerId, payload) {
    const res = await defaultAxiosInstance.put(
      `/companies/${companyId}/llm-keys/${providerId}`,
      payload
    );
    return res.data;
  }

  async deleteCompanyLLMKey(companyId, providerId) {
    const res = await defaultAxiosInstance.delete(
      `/companies/${companyId}/llm-keys/${providerId}`
    );
    return res.data;
  }

  // ── Entitlements editor (§4.1) ───────────────────────────────────────────
  /** → { company_id, permission_keys: string[] } */
  async getEntitlements(companyId) {
    const res = await defaultAxiosInstance.get(`/companies/${companyId}/entitlements`);
    return res.data;
  }

  /** Full replacement. Body: { permission_keys }. → same shape as GET. */
  async setEntitlements(companyId, permissionKeys) {
    const res = await defaultAxiosInstance.put(`/companies/${companyId}/entitlements`, {
      permission_keys: permissionKeys,
    });
    return res.data;
  }

  // ── LLM providers / models (catalog) ─────────────────────────────────────
  /** Array of providers (used by the company key matrix — asks for a big page). */
  async getProviders(activeOnly = true, opts = {}) {
    const res = await defaultAxiosInstance.get('/llm-providers/', {
      params: { active_only: activeOnly, limit: 200, ...listParams(opts) },
    });
    return res.data.providers || res.data;
  }

  /** → { providers, total, limit, offset } (searches name + display name) */
  async listProviders({ activeOnly = false, ...opts } = {}) {
    const res = await defaultAxiosInstance.get('/llm-providers/', {
      params: { active_only: activeOnly, ...listParams(opts) },
    });
    return res.data;
  }

  /** → { models, total, limit, offset } (searches name + display name + model id) */
  async listModels({ activeOnly = false, ...opts } = {}) {
    const res = await defaultAxiosInstance.get('/llm-models/', {
      params: { active_only: activeOnly, ...listParams(opts) },
    });
    return res.data;
  }

  // ── Permission catalog (§4.6) ────────────────────────────────────────────
  /** → { permissions: [{ key, label, description, scope, category }], total } */
  async getPermissionCatalog() {
    const res = await defaultAxiosInstance.get('/platform/permissions');
    return res.data;
  }

  // ── Users, cross-company (§4.2) ──────────────────────────────────────────
  /**
   * → { users, total, limit, offset } (searches email + first/last name).
   * `opts.partnerId` (Partner tier §4.8) = every user of every company of the
   * partner, direct users included; combinable with `companyId`.
   */
  async listUsers(companyId = null, { partnerId = null, ...opts } = {}) {
    const params = { ...listParams(opts) };
    if (companyId !== null && companyId !== undefined && companyId !== '') {
      params.company_id = companyId;
    }
    if (partnerId !== null && partnerId !== undefined && partnerId !== '') {
      params.partner_id = partnerId;
    }
    const res = await defaultAxiosInstance.get('/platform/users', { params });
    return res.data;
  }

  /** → { user: AdminUser, api_key: 'SHOWN-ONCE', roles: string[] } */
  async createUser(payload) {
    const res = await defaultAxiosInstance.post('/platform/users', payload);
    return res.data;
  }

  /** Body: any subset of { is_active, company_id, clear_company }. → AdminUser */
  async updateUser(userId, payload) {
    const res = await defaultAxiosInstance.patch(`/platform/users/${userId}`, payload);
    return res.data;
  }

  /** Assign a role (this is where company_admin is granted — platform only). */
  async assignUserRole(userId, roleId) {
    const res = await defaultAxiosInstance.post(`/platform/users/${userId}/roles`, {
      role_id: roleId,
    });
    return res.data;
  }

  async removeUserRole(userId, roleId) {
    const res = await defaultAxiosInstance.delete(`/platform/users/${userId}/roles/${roleId}`);
    return res.data;
  }

  /** → { user_id, api_key: 'NEW-KEY-SHOWN-ONCE' } */
  async resetUserApiKey(userId) {
    const res = await defaultAxiosInstance.post(`/platform/users/${userId}/reset-api-key`);
    return res.data;
  }

  // ── Roles, both tiers (§4.4) ─────────────────────────────────────────────
  /** → { roles, total, limit, offset } or Role[] (searches role name + description) */
  async listRoles({ companyId = null, includePlatformTier = true, ...opts } = {}) {
    const params = { include_platform_tier: includePlatformTier, ...listParams(opts) };
    if (companyId !== null && companyId !== undefined && companyId !== '') {
      params.company_id = companyId;
    }
    const res = await defaultAxiosInstance.get('/platform/roles', { params });
    return res.data;
  }

  async createRole(payload) {
    const res = await defaultAxiosInstance.post('/platform/roles', payload);
    return res.data;
  }

  async updateRole(roleId, payload) {
    const res = await defaultAxiosInstance.put(`/platform/roles/${roleId}`, payload);
    return res.data;
  }

  /** → { deleted: true, affected_users: N } */
  async deleteRole(roleId) {
    const res = await defaultAxiosInstance.delete(`/platform/roles/${roleId}`);
    return res.data;
  }

  // ── Audit log (§4.5) ─────────────────────────────────────────────────────
  /**
   * → { logs, total, limit, offset }. limit max is 200. Searches actor/action/target.
   * `partnerId` = everything done to the partner or any of its companies.
   */
  async listAuditLogs({
    companyId = null,
    partnerId = null,
    action = null,
    search = null,
    limit = 50,
    offset = 0,
  } = {}) {
    const params = { limit, offset };
    if (companyId !== null && companyId !== undefined && companyId !== '') {
      params.company_id = companyId;
    }
    if (partnerId !== null && partnerId !== undefined && partnerId !== '') {
      params.partner_id = partnerId;
    }
    if (action) params.action = action;
    if (search) params.search = search;
    const res = await defaultAxiosInstance.get('/platform/audit-logs', { params });
    return res.data;
  }

  // ── Partners (Partner tier guide §4) ─────────────────────────────────────
  // Auth: platform_partners (or `*`). A partner's hidden home company holds its
  // own staff; its keys are the partner's keys (/companies/{home}/llm-keys).

  /** → { partners: [{ id, name, is_active, home_company_id, company_count, user_count, ... }], total, limit, offset } */
  async listPartners({ activeOnly = false, ...opts } = {}) {
    const res = await defaultAxiosInstance.get('/platform/partners', {
      params: { active_only: activeOnly, ...listParams(opts) },
    });
    return res.data;
  }

  async getPartner(partnerId) {
    const res = await defaultAxiosInstance.get(`/platform/partners/${partnerId}`);
    return res.data;
  }

  /**
   * Body: { name, first_admin?: { email, first_name, last_name } }.
   * → { partner, first_admin: { user, api_key (SHOWN ONCE), roles } | null }
   */
  async createPartner(payload) {
    const res = await defaultAxiosInstance.post('/platform/partners', payload);
    return res.data;
  }

  /** Body: any subset of { name, is_active }. Deactivating locks out everyone under it. */
  async updatePartner(partnerId, payload) {
    const res = await defaultAxiosInstance.patch(`/platform/partners/${partnerId}`, payload);
    return res.data;
  }

  /** Same as PATCH { is_active: false }. */
  async deactivatePartner(partnerId) {
    const res = await defaultAxiosInstance.delete(`/platform/partners/${partnerId}`);
    return res.data;
  }

  /** → { partner_id, company_permission_keys: [], partner_permission_keys: [] } */
  async getPartnerEntitlements(partnerId) {
    const res = await defaultAxiosInstance.get(`/platform/partners/${partnerId}/entitlements`);
    return res.data;
  }

  /** Declarative: ONE list holding both company and partner keys. */
  async setPartnerEntitlements(partnerId, permissionKeys) {
    const res = await defaultAxiosInstance.put(`/platform/partners/${partnerId}/entitlements`, {
      permission_keys: permissionKeys,
    });
    return res.data;
  }

  /** Client companies only (the home company is excluded). → CompanyListResponse */
  async listPartnerCompanies(partnerId, opts = {}) {
    const res = await defaultAxiosInstance.get(`/platform/partners/${partnerId}/companies`, {
      params: listParams(opts),
    });
    return res.data;
  }

  /** Move a platform-direct company under the partner. */
  async attachPartnerCompany(partnerId, companyId) {
    const res = await defaultAxiosInstance.post(
      `/platform/partners/${partnerId}/companies/${companyId}`
    );
    return res.data;
  }

  /** The company becomes platform-direct again. */
  async detachPartnerCompany(partnerId, companyId) {
    const res = await defaultAxiosInstance.delete(
      `/platform/partners/${partnerId}/companies/${companyId}`
    );
    return res.data;
  }
}

const platformAdminService = new PlatformAdminService();
export default platformAdminService;
